import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Garage Electrical Installation Services',
  provider: {
    '@type': 'ElectricalContractor',
    name: 'Electrician Solutions Fresno',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Fresno',
      addressRegion: 'CA',
      addressCountry: 'US',
    },
  },
  areaServed: {
    '@type': 'City',
    name: 'Fresno',
    '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
  },
  description:
    'Professional garage electrical installation services in Fresno CA including garage wiring, outlets, lighting, EV charger circuits, workshop power, garage door opener circuits, and code compliance by licensed C-10 electricians',
  url: 'https://electriciansolutionsfresno.com/services/residential-electrical/garage-electrical',
};

export default function GarageElectricalPage() {
  const features = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Garage Lighting Installation',
      description:
        'Professional garage lighting installation — LED shop lights, recessed lights, motion-sensor lighting, and task lighting for workbenches and storage areas in Fresno garages',
    },
    {
      icon: 'ri-plug-line',
      title: 'Garage Outlet Installation',
      description:
        'Install GFCI-protected outlets throughout your garage — 120V outlets for tools and equipment, plus 240V outlets for welders, compressors, and EV chargers in Fresno homes',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'EV Charger Circuit Installation',
      description:
        'Dedicated 240V circuits for electric vehicle Level 2 chargers — 40A to 60A circuits with proper conduit, wiring, and panel capacity for Fresno garage EV charging',
    },
    {
      icon: 'ri-tools-line',
      title: 'Workshop Power Installation',
      description:
        'Complete electrical installations for garage workshops — dedicated circuits for power tools, table saws, air compressors, welders, and heavy equipment in Fresno garages',
    },
    {
      icon: 'ri-door-line',
      title: 'Garage Door Opener Circuits',
      description:
        'Professional electrical installation for garage door openers — dedicated circuits, proper outlet placement, and smart garage door opener wiring for Fresno homes',
    },
    {
      icon: 'ri-home-wifi-line',
      title: 'Garage Rewiring & Upgrades',
      description:
        'Complete garage rewiring and electrical upgrades — replace outdated wiring, add circuits, upgrade panels, and ensure code compliance for Fresno garage conversions',
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Garage Heater & AC Circuits',
      description:
        'Dedicated circuits for garage heaters, mini-split AC units, and climate control systems — keep your Fresno garage comfortable year-round with proper electrical installation',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Code Compliance & Safety',
      description:
        'Ensure all garage electrical work meets California codes — GFCI protection, proper grounding, circuit sizing, and local Fresno building requirements for safe garage electrical systems',
    },
  ];

  const whatsIncluded = [
    'Free garage electrical assessment and detailed project quote for Fresno properties',
    'Licensed C-10 electricians with expertise in garage electrical installations',
    'High-quality materials and equipment from trusted brands — Leviton, Lutron, Square D',
    'GFCI-protected outlets meeting California electrical codes for garage safety',
    'Proper circuit sizing for tools, equipment, EV chargers, and garage appliances',
    'Professional installation with clean conduit runs and organized wiring',
    'Complete testing and verification of all garage electrical work before completion',
    'Warranty on all labor and materials — your garage electrical system is guaranteed',
  ];

  return (
    <>
      <SEOHead
        title="Garage Electrical Installation Fresno CA | Garage Wiring Services"
        description="Professional garage electrical installation in Fresno, CA. Garage wiring, outlets, lighting, EV charger circuits, workshop power, garage door opener circuits. Licensed C-10 electricians. Free quotes."
        canonical="/services/residential-electrical/garage-electrical"
        keywords="garage electrical installation Fresno, garage wiring Fresno CA, garage outlet installation Fresno, EV charger circuit Fresno garage"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Garage Electrical Installation — Fresno, CA"
        heroSubtitle="Complete garage electrical services — lighting, outlets, EV chargers & workshop power"
        heroImage="https://public.readdy.ai/ai/img_res/5cfe010f72f7163fd8f93919f95c0728.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Professional%20electrician%20installing%20electrical%20outlet%20and%20conduit%20in%20garage%20wall%20with%20tools%20and%20equipment%20visible%20bright%20LED%20lighting%20California%20garage%20interior&width=700&height=600&seq=garage-fresno-outlet-06&orientation=portrait"
        tagline="Trusted by 500+ Fresno Homeowners"
        fresnoContent="Powering Fresno garages with professional electrical installation services that meet California building codes. Whether you're adding outlets and lighting to a Tower District garage, installing an EV charger circuit in Woodward Park, setting up a workshop in Fig Garden, upgrading garage electrical in Clovis, or converting a garage in Sunnyside, our licensed C-10 electricians provide complete garage electrical services. Many Fresno garages have minimal electrical infrastructure — just a single light and outlet. Modern garages need proper lighting, multiple GFCI-protected outlets, dedicated circuits for EV chargers and power tools, and electrical capacity for garage door openers, heaters, and equipment."
        features={features}
        whatsIncluded={whatsIncluded}
        faqs={[
          {
            question: 'What electrical outlets are required in Fresno garages?',
            answer:
              'California electrical code requires at least one GFCI-protected 120V outlet in every garage. However, most homeowners need multiple outlets for tools, equipment, and EV charging. We recommend installing GFCI outlets every 6-8 feet along garage walls, plus dedicated 240V outlets for EV chargers, welders, or air compressors. All garage outlets must be GFCI-protected for safety.',
          },
          {
            question: 'How much does garage electrical installation cost in Fresno?',
            answer:
              'Costs vary by project scope. Installing a single garage outlet costs $150–$250. Adding multiple outlets and lighting to a basic garage typically costs $800–$1,500. Complete garage electrical upgrades including lighting, outlets, and dedicated circuits range from $1,500–$3,500. EV charger circuit installation costs $500–$1,200 depending on distance from panel and amperage.',
          },
          {
            question: 'Can you install a circuit for an EV charger in my Fresno garage?',
            answer:
              "Yes, we specialize in EV charger circuit installation for Fresno garages. Level 2 EV chargers require dedicated 240V circuits (typically 40A to 60A) with proper wiring, conduit, and circuit breakers. We assess your electrical panel capacity, run conduit and wiring to your garage charging location, install the appropriate circuit breaker, and provide a NEMA 14-50 outlet or hardwired connection for your EV charger.",
          },
          {
            question: 'What type of lighting is best for Fresno garages?',
            answer:
              'LED shop lights are the most popular and efficient choice for garage lighting — they provide bright, even illumination with minimal energy use and long lifespan. We recommend 4-foot or 8-foot LED fixtures spaced every 4-6 feet for general lighting, plus task lighting over workbenches. Motion-sensor lights are great for convenience and energy savings.',
          },
          {
            question: 'Do I need a subpanel for my garage electrical in Fresno?',
            answer:
              "A garage subpanel is recommended if you're adding significant electrical loads like EV chargers, workshop equipment, heaters, or multiple circuits. Subpanels provide dedicated circuit capacity for your garage without overloading your main panel, and they make future garage electrical additions easier. Detached garages typically require subpanels.",
          },
          {
            question: 'Can you upgrade electrical in an older Fresno garage?',
            answer:
              'Absolutely. Many older Fresno garages have minimal electrical — just a single light and outlet with outdated wiring. We upgrade garage electrical systems by adding new circuits, installing multiple GFCI outlets, upgrading lighting, adding dedicated circuits for EV chargers or equipment, and ensuring all work meets current California electrical codes.',
          },
        ]}
        serviceSlug="garage-electrical"
      />
    </>
  );
}
